<template>
    <main-dialog
        ref="dialog"
        heading="parkingLot.allocatePromotion.heading"
        btn-confirm-text="actions.copy"
        title-text-align="left"
        :has-activator="hasActivator"
        :confirm-btn-disabled="disabled"
        @confirm="allocatePromotionsDialogConfirm"
        @close="closeDialog"
    >
        <template v-if="hasActivator" v-slot:actions="{ activator }">
            <icon v-bind="activator" icon-name="duplicate" small @click.stop />
        </template>
        <template v-slot:default>
            <div class="allocate-description">
                {{ $t('parkingLot.allocatePromotion.pastPromotionsDescription') }}
            </div>
            <div class="allocate-destination">
                {{ $t('parkingLot.allocatePromotion.destination') }}:
            </div>
            <v-container class="allocate-past-promotion">
                <v-row density="compact" gap="compact">
                    <v-col
                        cols="4"
                        density="compact"
                        class="row-allocate-past-promotion__radio align-self-center"
                    >
                        <v-radio-group
                            v-model="selectedType"
                            hide-details="auto"
                            class="ag-radio-group"
                        >
                            <v-radio
                                :label="
                                    toSentenceCase($t('parkingLot.allocatePromotion.parkingLot'))
                                "
                                :value="parkingLot"
                                class="pass-radio"
                                @click="selectedType = parkingLot"
                            />
                        </v-radio-group>
                    </v-col>
                    <v-col density="compact" class="align-self-center">
                        <vuex-date-range
                            :key="dateRange.length"
                            :from="{ fieldName: 'from' }"
                            :to="{ fieldName: 'to' }"
                            :use-vuex-staging-area="false"
                            :getter="() => dateRange"
                            :min-date="minDateRangeDate"
                            @change="dateRangeSelected"
                        />
                    </v-col>
                </v-row>
            </v-container>
            <div v-if="requestInProgress" class="in-progress--message">
                <label class="in-progress__message">{{
                    toSentenceCase($t('parkingLot.allocatePromotion.inProgressMessage'))
                }}</label>
                <spinner-dynamic />
            </div>
            <v-overlay
                v-if="requestInProgress"
                class="allocate-past-promotion__overlay"
                :absolute="true"
                :value="true"
                :opacity="0.3"
            />
        </template>
    </main-dialog>
</template>

<script>
import to from 'await-to-js';
import resources from '@enums/resources';
import { mapActions, mapState } from 'vuex';

const { parkingLot } = resources;

export default {
    props: {
        hasActivator: {
            type: Boolean,
            default: true,
        },
        promotionIds: {
            type: Array,
            required: true,
        },
    },
    emits: ['confirm'],
    data() {
        return {
            selectedType: parkingLot,
            selectedStartDate: null,
            selectedEndDate: null,
            parkingLot,
            dateRange: [],
        };
    },
    computed: {
        ...mapState('promotions', ['loading']),
        minDateRangeDate() {
            return this.datepickerFormat(this.$moment());
        },
        disabled() {
            return this.selectedStartDate === null || this.selectedEndDate === null;
        },
        requestInProgress() {
            return this.loading;
        },
    },
    methods: {
        ...mapActions('promotions', ['movePromotionsToParkingLot']),

        openDialog() {
            this.$refs.dialog.open();
        },
        closeDialog() {
            this.resetDateRange();
            this.$refs.dialog.close();
        },
        async allocatePromotionsDialogConfirm() {
            const [error] = await to(
                this.movePromotionsToParkingLot({
                    promotionIds: this.promotionIds,
                    startDate: this.selectedStartDate,
                    endDate: this.selectedEndDate,
                    copyPromotions: true,
                    sendUpdatedDates: true,
                    refetchDocs: false,
                    updateForecastReference: true,
                    removeNonPromotableProducts: true,
                })
            );

            if (!error) {
                this.closeDialog();
                this.$emit('confirm');
            }
        },
        dateRangeSelected({ from, to: toValue }) {
            this.selectedStartDate = from;
            this.selectedEndDate = toValue;
            this.dateRange = [this.selectedStartDate, this.selectedEndDate];
        },
        resetDateRange() {
            this.selectedStartDate = null;
            this.selectedEndDate = null;
            this.dateRange.splice(0);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.allocate-destination {
    font-size: 1.2rem;
    font-weight: 600;
}

.allocate-description {
    font-size: 1.2rem;
}

.row-allocate-past-promotion__radio {
    padding: 0 !important;
}

:deep(.allocate-past-promotion__overlay) {
    z-index: 99 !important;
}

:deep(.pass-radio) {
    margin-top: 0 !important;
}

.in-progress__message {
    font-size: 1.3rem;
    color: $promo-text-colour;
    margin: 1rem;

    .spinner {
        width: 1.4rem;
        height: 1.4rem;
    }
}

:deep(.v-text-field__slot) {
    label {
        padding-left: 1rem;
    }
}

:deep(.v-input--selection-controls) {
    margin-top: 1.3rem;
    margin-bottom: 0.5rem;
}
</style>
